import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { ragAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import ExplainMetrics from '../../components/common/ExplainMetrics';
import MetricHint from '../../components/common/MetricHint';
import { formatDate, plural } from '../../utils/format';

// Unified knowledge sources view — WATI-parity. Replaces the split of
// "Documents" (uploaded files) and "WebScraper" (scraped URLs) with one
// scannable table. Web pages can be refreshed in place; file replacement
// remains on the specialised Upload page.

const SOURCE_METRIC_DEFINITIONS = [
  {
    name: 'Source',
    definition: 'A page from your website or a file you uploaded. Your agent answers from these.',
  },
  {
    name: 'Pages',
    definition: 'Each website page or each page of an uploaded document counts as one page. Your plan includes a number of pages.',
  },
  {
    name: 'Knowledge pages used',
    definition: 'All pages your agent can answer from, against the number your plan includes. Remove pages you no longer need, or upgrade, as you near the limit.',
  },
  {
    name: 'Refresh from website',
    definition: 'Reads the page from your website again, so your agent answers from the latest version.',
    note: 'Files update when you upload them again under the same name.',
  },
];

const fmtCount = (n) => (Number.isFinite(n) ? Number(n).toLocaleString() : '0');

const typeBadge = (t) => {
  const isWeb = t === 'web_scrape';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
        isWeb
          ? 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200'
          : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      }`}
    >
      {isWeb ? <GlobeAltIcon className="w-3 h-3" /> : <DocumentTextIcon className="w-3 h-3" />}
      {isWeb ? 'Web' : 'File'}
    </span>
  );
};

const sourceKey = (s) => `${s.collection_id}::${s.source}`;

// One host normalisation, used for BOTH stored sources and the URL typed into
// the Add-page dialog, and deliberately equivalent to website_domain() in
// services/source_identity.py.
//
// `new URL` already applies IDNA, so bucher.de arrives as xn--bcher-kva.de, but
// it keeps a fully-qualified root dot. Without stripping that, a held
// "example.com" source and a typed "example.com." URL were the same domain to
// the backend and different domains to this dialog -- so Add stayed disabled on
// a URL the API would have accepted.
const normalizeHost = (value) => {
  try {
    return new URL(value).hostname.toLowerCase().replace(/\.$/, '').replace(/^www\./, '');
  } catch (err) {
    return null;
  }
};

// A page with real content produces several chunks. One or two means the
// scraper captured a fraction of it — and nothing errored, so the row looks
// healthy. serviti.ai's own homepage ingested as ONE chunk from 826 words of
// text, and the only visible symptom was this number.
const isUnderExtracted = (s) =>
  s.source_type === 'web_scrape' && Number(s.chunks) > 0 && Number(s.chunks) <= 2;

export default function KnowledgeSourcesPage() {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const user = useAuthStore((state) => state.user);
  const isSuperadmin = Boolean(user?.is_superuser || user?.role === 'superadmin');
  const canManageSources = Boolean(
    user?.is_superuser ||
    user?.role === 'superadmin' ||
    user?.tenant_role === 'owner' ||
    user?.tenant_role === 'admin' ||
    (user?.role === 'admin' && user?.tenant_id)
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [refreshingKey, setRefreshingKey] = useState(null);
  const refreshInFlightRef = useRef(false);
  const [rescrapeTarget, setRescrapeTarget] = useState(null);
  const [reuploadTarget, setReuploadTarget] = useState(null);
  const [textTarget, setTextTarget] = useState(null);
  const [addPageOpen, setAddPageOpen] = useState(false);
  const [addPageUrl, setAddPageUrl] = useState('');
  const [addPageBusy, setAddPageBusy] = useState(false);
  const [addPageCollectionId, setAddPageCollectionId] = useState('');
  const tenantRagPath = (page) => tenantId
    ? `/t/${tenantId}/rag/${page}`
    : `/platform/rag/${page}`;

  const refreshPage = async (source) => {
    const key = sourceKey(source);
    if (!canManageSources || !source.scrape_job_id || refreshInFlightRef.current) return;

    refreshInFlightRef.current = true;
    setRefreshingKey(key);
    try {
      const response = await ragAPI.refreshScrapeJob(source.scrape_job_id, {
        scope: 'page',
        source: source.source,
      });
      const refreshJobId = response.data?.job_id;
      if (!refreshJobId) {
        throw new Error('Refresh started without a job ID');
      }
      toast.success(response.data?.reused ? 'Page refresh is already running' : 'Page refresh started');
      const scraperPath = tenantId
        ? `/t/${tenantId}/rag/web-scraper`
        : '/platform/rag/web-scraper';
      navigate(`${scraperPath}?job=${encodeURIComponent(refreshJobId)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to refresh page');
      refreshInFlightRef.current = false;
      setRefreshingKey(null);
    }
  };

  // Add one more page of a site the tenant already has. A one-URL crawl
  // (max_pages 1, depth 0) rather than a re-crawl, so it costs one knowledge
  // page and no website slot -- the cap counts domains, and this domain is
  // already held.
  // host -> the collections that host already lives in. A domain can appear in
  // more than one collection (different agents), and silently picking the first
  // would drop the page into the wrong agent's knowledge base, so the dialog
  // makes the destination explicit whenever there is a choice.
  const knownDomains = useMemo(() => {
    const domains = new Map();
    (data?.sources || []).forEach((s) => {
      if (s.source_type !== 'web_scrape') return;
      const host = normalizeHost(s.source);
      if (!host) return; /* a source that will not parse as a URL cannot anchor a page */
      const seen = domains.get(host) || new Map();
      if (!seen.has(s.collection_id)) seen.set(s.collection_id, s);
      domains.set(host, seen);
    });
    return domains;
  }, [data]);

  const addPageDomain = normalizeHost(addPageUrl);
  const addPageCollections = addPageDomain
    ? Array.from((knownDomains.get(addPageDomain) || new Map()).values())
    : [];
  const addPageAnchor =
    addPageCollections.find((c) => c.collection_id === addPageCollectionId) ||
    (addPageCollections.length === 1 ? addPageCollections[0] : null);

  const submitAddPage = async () => {
    if (!addPageAnchor || addPageBusy) return;
    setAddPageBusy(true);
    try {
      const response = await ragAPI.scrapeWebsite({
        url: addPageUrl.trim(),
        collection_id: addPageAnchor.collection_id,
        max_pages: 1,
        max_depth: 0,
        use_sitemap: false,
      });
      const jobId = response.data?.job_id;
      if (!jobId) throw new Error('Scrape started without a job ID');
      toast.success('Adding page');
      const scraperPath = tenantId
        ? `/t/${tenantId}/rag/web-scraper`
        : '/platform/rag/web-scraper';
      navigate(`${scraperPath}?job=${encodeURIComponent(jobId)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Could not add that page');
      setAddPageBusy(false);
    }
  };

  const openSourceText = async (source) => {
    setTextTarget({ source, loading: true, data: null, error: null });
    try {
      const response = await ragAPI.getSourceText(source.collection_id, source.source);
      setTextTarget({ source, loading: false, data: response.data, error: null });
    } catch (err) {
      setTextTarget({
        source,
        loading: false,
        data: null,
        error: err?.response?.data?.detail || err.message || 'Could not load the indexed text',
      });
    }
  };

  const confirmRescrape = async () => {
    const source = rescrapeTarget;
    if (!source) return;
    setRescrapeTarget(null);
    await refreshPage(source);
  };

  const confirmDelete = async () => {
    const s = deleteTarget;
    if (!s) return;
    setDeletingKey(sourceKey(s));
    try {
      await ragAPI.deleteDocument(s.collection_id, s.source);
      toast.success(`Removed "${s.title || s.source}" (${s.chunks} chunk${s.chunks === 1 ? '' : 's'})`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete source');
    } finally {
      setDeletingKey(null);
    }
  };

  const load = () => {
    setLoading(true);
    setError(null);
    ragAPI
      .getKnowledgeSources()
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message || 'Failed to load sources'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!data?.sources) return [];
    const q = query.trim().toLowerCase();
    return data.sources.filter((s) => {
      if (typeFilter === 'web' && s.source_type !== 'web_scrape') return false;
      if (typeFilter === 'file' && s.source_type === 'web_scrape') return false;
      if (!q) return true;
      return (
        (s.source || '').toLowerCase().includes(q) ||
        (s.title || '').toLowerCase().includes(q) ||
        (s.collection_display_name || '').toLowerCase().includes(q)
      );
    });
  }, [data, query, typeFilter]);

  const usage = data?.usage;
  const usagePct = usage?.usage_pct ?? 0;
  const usageBarColor =
    usagePct >= 90
      ? 'bg-red-500'
      : usagePct >= 70
      ? 'bg-amber-500'
      : 'bg-indigo-500';

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BookOpenIcon className="w-6 h-6 text-indigo-500" />
            Business knowledge
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Every website page and file your AI agent can use when answering customers.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExplainMetrics items={SOURCE_METRIC_DEFINITIONS} />
          {isSuperadmin && (
            <Link
              to={tenantRagPath('collections')}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white px-2 py-1.5"
              title="Create, rename, or remove knowledge collections"
            >
              Advanced settings
            </Link>
          )}
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-md px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
            title="Reload the source list"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Reload list
          </button>
          {canManageSources && knownDomains.size > 0 && (
            <button
              type="button"
              onClick={() => { setAddPageUrl(''); setAddPageOpen(true); }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-md px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
              title="Add one more page from a website you already added"
            >
              <GlobeAltIcon className="w-4 h-4" />
              Add page
            </button>
          )}
          <Link
            to={tenantRagPath('upload')}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md px-3 py-1.5"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            Add source
          </Link>
        </div>
      </div>

      {/* Knowledge pages usage bar */}
      {usage && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1">
              Knowledge pages used
              <MetricHint text="One knowledge page = one uploaded document page or one scraped website page. Tier caps are enforced on this number." />
            </div>
            <div className="text-sm text-gray-800 dark:text-gray-100 tabular-nums">
              <span className="font-semibold">{fmtCount(usage.pages)}</span>
              <span className="text-gray-500 dark:text-gray-400"> of {fmtCount(usage.max_pages)} pages used ({usagePct}%)</span>
            </div>
          </div>
          <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full ${usageBarColor} rounded-full transition-all`}
              style={{ width: `${Math.min(usagePct, 100)}%` }}
            />
          </div>
          {usagePct >= 70 && (
            <div className="mt-2 text-xs">
              <Link
                to={tenantId ? `/t/${tenantId}/billing` : '/platform/billing'}
                className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
              >
                See plans with more pages →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Search + filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 mb-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by page address or title…"
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5"
          >
            <option value="all">All types</option>
            <option value="web">Web pages</option>
            <option value="file">Uploaded files</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading sources…</div>
      )}

      {/* Table */}
      {data && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <th className="py-2 pl-4 pr-3">Source</th>
                  <th className="py-2 px-3">Type</th>
                  {isSuperadmin && <th className="py-2 px-3">Collection</th>}
                  {isSuperadmin && <th className="py-2 px-3 text-right">Chunks</th>}
                  <th className="py-2 px-3 text-right">Pages</th>
                  {isSuperadmin
                    ? <th className="py-2 px-3">Content</th>
                    : <th className="py-2 px-3">Last updated</th>}
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right pr-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperadmin ? 8 : 6} className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      {data.total === 0
                        ? 'Nothing added yet. Add your website or upload a file to get started.'
                        : 'No sources match the current filter.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((s, idx) => (
                    <tr key={`${s.collection_id}-${s.source}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-2 pl-4 pr-3 max-w-md">
                        <div className="truncate font-medium text-gray-900 dark:text-white" title={s.source}>
                          {s.title || s.source}
                        </div>
                        {s.title && s.source && s.title !== s.source && (
                          <div className="truncate text-xs text-gray-500 dark:text-gray-400" title={s.source}>
                            {s.source}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3">{typeBadge(s.source_type)}</td>
                      {isSuperadmin && (
                      <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                        <Link
                          to={`/rag/collections?id=${encodeURIComponent(s.collection_id)}`}
                          className="text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          {s.collection_display_name || s.collection_name || s.collection_id.slice(0, 8)}
                        </Link>
                      </td>
                      )}
                      {isSuperadmin && (
                      <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                        <span className="inline-flex items-center gap-1 justify-end">
                          {s.chunks}
                          {isUnderExtracted(s) && (
                            <span
                              className="text-amber-600 dark:text-amber-400"
                              title={
                                'Only ' + s.chunks + ' chunk(s) from this page. A page with real content ' +
                                'normally produces several. The scraper may have captured a fraction of it — ' +
                                'the page will look fine and the agent will still say "I don\'t have that ' +
                                'information". Re-scrape it, or upload the content as Markdown instead.'
                              }
                            >
                              <ExclamationTriangleIcon className="w-4 h-4" />
                            </span>
                          )}
                        </span>
                      </td>
                      )}
                      <td className="py-2 px-3 text-right tabular-nums text-gray-500 dark:text-gray-400">
                        {fmtCount(s.pages)}
                      </td>
                      {!isSuperadmin && (
                        <td className="py-2 px-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                          {formatDate(s.updated_at)}
                        </td>
                      )}
                      {isSuperadmin && (
                      <td className="py-2 px-3 whitespace-nowrap">
                        {s.content_hash ? (
                          <div className="flex flex-col gap-0.5">
                            <button
                              type="button"
                              onClick={() => openSourceText(s)}
                              className="font-mono text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline text-left"
                              title="Show the text actually indexed for this source"
                            >
                              {s.content_hash.slice(0, 8)}
                            </button>
                            {s.previous_content_hash && s.previous_content_hash !== s.content_hash ? (
                              <span
                                className="font-mono text-[10px] text-amber-700 dark:text-amber-400"
                                title={`Replaced ${s.previous_content_hash}`}
                              >
                                was {s.previous_content_hash.slice(0, 8)}
                              </span>
                            ) : (
                              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {s.previous_content_hash ? 'unchanged' : 'first ingest'}
                              </span>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openSourceText(s)}
                            className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline"
                            title="Show the text actually indexed for this source"
                          >
                            View text
                          </button>
                        )}
                      </td>
                      )}
                      <td className="py-2 px-3 whitespace-nowrap">
                        {isUnderExtracted(s) ? (
                          <span
                            className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                            title="Very little text came through from this page, so your agent may not know what it says. Refresh it, or upload the text as a file."
                          >
                            Check this page
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-200">
                            Ready
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 pr-4 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-3">
                          {s.source_type === 'web_scrape' ? (
                            canManageSources && (
                              <button
                                type="button"
                                onClick={() => setRescrapeTarget(s)}
                                disabled={Boolean(refreshingKey) || Boolean(deletingKey) || !s.scrape_job_id}
                                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                                title={
                                  s.scrape_job_id
                                    ? 'Fetch this page again and atomically replace its indexed content'
                                    : 'Refresh is unavailable because the original scrape job is not recorded'
                                }
                              >
                                <ArrowPathIcon
                                  className={`w-3 h-3 ${refreshingKey === sourceKey(s) ? 'animate-spin' : ''}`}
                                />
                                {refreshingKey === sourceKey(s) ? 'Starting…' : 'Refresh from website'}
                              </button>
                            )
                          ) : (
                            <button
                              type="button"
                              onClick={() => setReuploadTarget(s)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                              title="Explains how re-uploading replaces this file, then opens the Upload page"
                            >
                              <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                              Re-upload
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteTarget(s)}
                            disabled={deletingKey === sourceKey(s) || refreshingKey === sourceKey(s)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Remove this page or file from your agent's knowledge"
                          >
                            <TrashIcon className="w-3 h-3" />
                            {deletingKey === sourceKey(s) ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {data && (
            <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
              <span>
                {filtered.length} of {plural(data.total, 'source')}
              </span>
              {isSuperadmin && data.warnings?.length > 0 && (
                <span className="text-amber-700 dark:text-amber-400" title={data.warnings.join('\n')}>
                  ⚠ {data.warnings.length} collection{data.warnings.length === 1 ? '' : 's'} could not be indexed
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation. Shows the chunk count about to be destroyed —
          this removes embeddings from the vector store and cannot be undone;
          the source has to be re-ingested. */}
      {addPageOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <GlobeAltIcon className="w-5 h-5 text-indigo-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add a page</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Scrapes one page from a site you already have. No crawl, no link
                following — just this URL.
              </p>
              <input
                type="url"
                value={addPageUrl}
                onChange={(e) => { setAddPageUrl(e.target.value); setAddPageCollectionId(''); }}
                placeholder={`https://${Array.from(knownDomains.keys())[0] || 'example.com'}/faq`}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Your sites:{' '}
                <span className="font-mono">{Array.from(knownDomains.keys()).join(', ')}</span>
              </p>
              {addPageUrl.trim() && !addPageAnchor && (
                <div className="rounded-md border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    {addPageDomain
                      ? `${addPageDomain} is a different website. Adding it would use a website slot from your plan — start it from the Web Scraper page instead.`
                      : 'That does not look like a full URL. Include https://'}
                  </p>
                </div>
              )}
              {addPageCollections.length > 1 && (
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                    {addPageDomain} is in {addPageCollections.length} collections — pick the
                    destination
                  </label>
                  <select
                    value={addPageCollectionId}
                    onChange={(e) => setAddPageCollectionId(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  >
                    <option value="">Select a collection…</option>
                    {addPageCollections.map((c) => (
                      <option key={c.collection_id} value={c.collection_id}>
                        {c.collection_display_name || c.collection_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {addPageAnchor && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Goes into{' '}
                  <span className="font-mono">
                    {addPageAnchor.collection_display_name || addPageAnchor.collection_name}
                  </span>
                  , alongside your other {addPageDomain} pages. Costs 1 knowledge page and no
                  website slot.
                </p>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setAddPageOpen(false)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={submitAddPage}
                disabled={!addPageAnchor || addPageBusy}
                className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addPageBusy ? 'Starting…' : 'Add page'}
              </button>
            </div>
          </div>
        </div>
      )}

      {textTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 flex flex-col max-h-[85vh]">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Indexed text</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">
                {textTarget.source.source}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                This is what your agent retrieves from — not what the page serves now.
              </p>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              {textTarget.loading && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
              )}
              {textTarget.error && (
                <p className="text-sm text-red-600 dark:text-red-400">{textTarget.error}</p>
              )}
              {textTarget.data && (
                <>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                    <span>
                      chunks stored:{' '}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {textTarget.data.chunk_count}
                      </span>
                      {textTarget.data.registry_chunk_count !== textTarget.data.chunk_count && (
                        <span className="text-amber-700 dark:text-amber-400">
                          {' '}(registry says {textTarget.data.registry_chunk_count})
                        </span>
                      )}
                    </span>
                    {textTarget.data.content_hash && (
                      <span className="font-mono">hash {textTarget.data.content_hash.slice(0, 12)}</span>
                    )}
                    {textTarget.data.previous_content_hash && (
                      <span className="font-mono">
                        was {textTarget.data.previous_content_hash.slice(0, 12)}
                      </span>
                    )}
                  </div>
                  {!textTarget.data.durable_text_available && (
                    <div className="rounded-md border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                      <p className="text-xs text-amber-800 dark:text-amber-300">
                        No stored text for this source. Its vectors are live and searchable,
                        but the durable copy predates the current ingest path — rescrape or
                        re-upload it to capture the text here.
                      </p>
                    </div>
                  )}
                  {textTarget.data.chunks.map((chunk) => (
                    <div key={chunk.chunk_index} className="rounded-md border border-gray-200 dark:border-gray-700">
                      <div className="px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                        chunk {chunk.chunk_index}
                      </div>
                      <pre className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words font-sans">
                        {chunk.text}
                      </pre>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setTextTarget(null)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {reuploadTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <ArrowTopRightOnSquareIcon className="w-5 h-5 text-indigo-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Re-upload this file?</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300 break-all">
                <span className="font-mono text-xs">{reuploadTarget.source}</span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Serviti cannot fetch a file the way it re-fetches a URL, so this opens the
                Upload page. Nothing changes until you actually upload.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Upload it under the{' '}
                <span className="font-semibold text-gray-900 dark:text-white">same file name</span>{' '}
                and it replaces what your agent knows from this file.
              </p>
              <div className="rounded-md border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  The file name is how Serviti identifies this source, and it is
                  case-sensitive. Uploading the same content as{' '}
                  <span className="font-mono">v2</span>, or with different capitalisation,
                  adds a second source instead of replacing this one — leaving your agent
                  answering from both copies.
                </p>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setReuploadTarget(null)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => { setReuploadTarget(null); navigate(tenantRagPath('upload')); }}
                className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700"
              >
                Continue to upload
              </button>
            </div>
          </div>
        </div>
      )}

      {rescrapeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <ArrowPathIcon className="w-5 h-5 text-indigo-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Refresh this page from your website?</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300 break-all">
                <span className="font-medium">{rescrapeTarget.title || rescrapeTarget.source}</span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Serviti reads this page from your website again and{' '}
                <span className="font-semibold text-gray-900 dark:text-white">replaces</span> what your
                agent knows from it. Refreshing never creates a duplicate, and if the page has not
                changed, nothing changes.
              </p>
              <div className="rounded-md border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  Content stays filed under this exact URL. If your site now serves this
                  page at a different address, a later whole-site scrape will index it
                  again as a separate source — leaving two copies of the same page.
                </p>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setRescrapeTarget(null)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmRescrape}
                disabled={Boolean(refreshingKey)}
                className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {refreshingKey ? 'Starting…' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <TrashIcon className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Remove this source?</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300 break-all">
                <span className="font-medium">{deleteTarget.title || deleteTarget.source}</span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your agent will no longer be able to answer from this page or file.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                This cannot be undone. To bring it back, add the page or upload the file again.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={!!deletingKey}
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingKey ? 'Deleting…' : 'Delete source'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
